import { useEffect, useRef } from 'react';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';

function reveal(isVisible: boolean, extra = '') {
  return `${extra} fade-up ${isVisible ? 'revealed' : ''}`.trim();
}

export function AskSection() {
  const [ref, isVisible] = useIntersectionObserver<HTMLElement>({ threshold: 0.1 });

  // Timeline animation
  const timelineRef = useRef<HTMLDivElement>(null);
  const hasAnimatedTimeline = useRef(false);

  useEffect(() => {
    if (!isVisible || hasAnimatedTimeline.current) return;
    hasAnimatedTimeline.current = true;

    const timeline = timelineRef.current;
    if (!timeline) return;

    const progress = timeline.querySelector<HTMLElement>('#timelineProgress');
    const nodes = timeline.querySelectorAll('.timeline-node');
    const totalNodes = nodes.length;

    nodes.forEach((node, i) => {
      setTimeout(() => {
        node.classList.add('active');
        if (progress) {
          (progress as any).value = (i + 1) / totalNodes;
        }
      }, i * 400);
    });
  }, [isVisible]);

  return (
    <section id="ask" className="section ask-section" aria-label="The Ask: Investment Opportunity" ref={ref}>
      <div className="section-inner">
        <h2 className={reveal(isVisible, 'section-title')}>The Ask</h2>
        <p className={reveal(isVisible, 'section-subtitle')} style={{ '--stagger': 1 } as React.CSSProperties}>Seeking pre-seed investment and strategic partners.</p>

        <div className="ask-columns">
          <div className={`ask-col slide-left ${isVisible ? 'revealed' : ''}`}>
            <h3>What We&apos;re Building</h3>
            <md-list className="ask-list">
              <md-list-item>
                <md-icon slot="start">rocket_launch</md-icon>
                <div slot="headline">Scaffold MVP</div>
                <div slot="supporting-text">16-week coding bootcamp with Scout AI tutor, spaced repetition engine, and process-based assessment, all powered by Google Gemini</div>
              </md-list-item>
              <md-list-item>
                <md-icon slot="start">science</md-icon>
                <div slot="headline">First Cohort Proof</div>
                <div slot="supporting-text">20–50 learners generating quantified evidence: retention rates, transfer ability, cognitive load metrics, and learning signal depth</div>
              </md-list-item>
              <md-list-item>
                <md-icon slot="start">hub</md-icon>
                <div slot="headline">Platform Extraction</div>
                <div slot="supporting-text">Extract the learning engine into infrastructure any institution can deploy, from bootcamp to platform to infrastructure layer</div>
              </md-list-item>
            </md-list>
          </div>

          <div className={`ask-col slide-right ${isVisible ? 'revealed' : ''}`}>
            <h3>Why This Investment</h3>
            <md-list className="ask-list">
              <md-list-item>
                <md-icon slot="start">trending_up</md-icon>
                <div slot="headline">$400B+ Addressable Market</div>
                <div slot="supporting-text">Corporate training, AI tutoring, and workforce upskilling, converging with no clear winner</div>
              </md-list-item>
              <md-list-item>
                <md-icon slot="start">database</md-icon>
                <div slot="headline">Proprietary Data Moat</div>
                <div slot="supporting-text">Process-based assessment generates learning signal no competitor can replicate: a data moat that deepens with every learner</div>
              </md-list-item>
              <md-list-item>
                <md-icon slot="start">shield</md-icon>
                <div slot="headline">Three-Pillar Moat</div>
                <div slot="supporting-text">Competitors can copy one pillar. None can combine all three: active learning + Google Gemini + human connection</div>
              </md-list-item>
              <md-list-item>
                <md-icon slot="start">monitoring</md-icon>
                <div slot="headline">Validated Exit Landscape</div>
                <div slot="supporting-text">Sana Labs $1.1B, Instructure $4.8B, PowerSchool $5.6B, Duolingo ~$5.2B. The market rewards learning infrastructure</div>
              </md-list-item>
            </md-list>
          </div>
        </div>

        {/* Phased milestones */}
        <div className={reveal(isVisible, 'ask-timeline')} ref={timelineRef}>
          <h3>Phased Milestones</h3>
          <div className="timeline-bar">
            <md-linear-progress id="timelineProgress" value="0" aria-label="Investment milestone progress"></md-linear-progress>
          </div>
          <div className="timeline-phases" data-stagger>
            {[
              { phase: 0, desc: 'Build Scaffold MVP + Scout AI on Gemini' },
              { phase: 1, desc: 'Closed Beta: 20–50 learners, measure everything' },
              { phase: 2, desc: 'First Paid Cohort: prove unit economics' },
              { phase: 3, desc: 'Platform extraction: infrastructure for any institution' },
            ].map(({ phase, desc }) => (
              <div key={phase} className={reveal(isVisible, 'timeline-node')} data-phase={phase}>
                <div className="node-dot"></div>
                <span className="node-label">Phase {phase}</span>
                <span className="node-desc">{desc}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
